// Serviço do domínio protocols — motor genérico de preparação de consulta.
// Versões publicadas são imutáveis (RLS); edição acontece sempre em rascunho.
import { createClient } from '@/lib/supabase/client'
import type { Protocolo, ProtocoloItem, ProtocoloVersao } from '@/lib/types'
import { registrarAuditoria } from '@/services/identity'

export interface ProtocoloComVersao extends Protocolo {
  versao_atual: ProtocoloVersao | null   // última publicada
  rascunho: ProtocoloVersao | null       // última não publicada
}

export async function listProtocolos(workspaceId: string): Promise<ProtocoloComVersao[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('protocolos')
    .select('*, protocolo_versoes(*)')
    .eq('workspace_id', workspaceId)
    .order('nome')
  if (error) throw error
  return (data ?? []).map(row => {
    const versoes = ((row as Record<string, unknown>).protocolo_versoes as ProtocoloVersao[] | null) ?? []
    versoes.sort((a, b) => b.numero - a.numero)
    const { protocolo_versoes: _drop, ...resto } = row as Record<string, unknown>
    void _drop
    return {
      ...(resto as unknown as Protocolo),
      versao_atual: versoes.find(v => v.publicada) ?? null,
      rascunho: versoes.find(v => !v.publicada) ?? null,
    }
  })
}

export async function criarProtocolo(params: {
  workspaceId: string
  nome: string
  tipoConsulta: 'primeira' | 'retorno' | 'ambas'
  especialidade?: string | null
}): Promise<string> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('protocolos')
    .insert({
      workspace_id: params.workspaceId,
      nome: params.nome.trim(),
      tipo_consulta: params.tipoConsulta,
      especialidade: params.especialidade ?? null,
    })
    .select('id')
    .single()
  if (error) throw error
  const id = (data as { id: string }).id
  const { error: e2 } = await supabase.from('protocolo_versoes').insert({
    workspace_id: params.workspaceId,
    protocolo_id: id,
    numero: 1,
    publicada: false,
    itens: [],
  })
  if (e2) throw e2
  await registrarAuditoria({ workspaceId: params.workspaceId, acao: 'protocolo_criado', entidade: 'protocolos', registroId: id })
  return id
}

export async function atualizarProtocolo(params: {
  workspaceId: string
  id: string
  dados: Partial<Pick<Protocolo, 'nome' | 'tipo_consulta' | 'especialidade' | 'ativo' | 'descricao'>>
}): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('protocolos')
    .update(params.dados)
    .eq('id', params.id)
    .eq('workspace_id', params.workspaceId)
  if (error) throw error
}

export async function salvarItensRascunho(params: {
  workspaceId: string
  versaoId: string
  itens: ProtocoloItem[]
}): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('protocolo_versoes')
    .update({ itens: params.itens })
    .eq('id', params.versaoId)
    .eq('workspace_id', params.workspaceId)
  if (error) throw error
}

export async function publicarVersao(params: {
  workspaceId: string
  protocoloId: string
  versaoId: string
}): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('protocolo_versoes')
    .update({ publicada: true })
    .eq('id', params.versaoId)
    .eq('workspace_id', params.workspaceId)
  if (error) throw error
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'protocolo_publicado',
    entidade: 'protocolo_versoes',
    registroId: params.versaoId,
  })
}

/** Edição de protocolo já publicado: cria a próxima versão em rascunho copiando os itens. */
export async function novaVersaoRascunho(params: {
  workspaceId: string
  protocolo: ProtocoloComVersao
}): Promise<ProtocoloVersao> {
  const supabase = createClient()
  const base = params.protocolo.versao_atual
  const numero = Math.max(base?.numero ?? 0, params.protocolo.rascunho?.numero ?? 0) + 1
  const { data, error } = await supabase
    .from('protocolo_versoes')
    .insert({
      workspace_id: params.workspaceId,
      protocolo_id: params.protocolo.id,
      numero,
      publicada: false,
      itens: base?.itens ?? [],
    })
    .select('*')
    .single()
  if (error) throw error
  return data as ProtocoloVersao
}

/** Protocolo publicado que se aplica a uma consulta (match por tipo; mais específico primeiro). */
export async function encontrarProtocoloParaConsulta(params: {
  workspaceId: string
  tipo: 'primeira' | 'retorno'
}): Promise<{ protocolo: Protocolo; versao: ProtocoloVersao } | null> {
  const lista = await listProtocolos(params.workspaceId)
  const candidatos = lista.filter(p => p.ativo && p.versao_atual &&
    (p.tipo_consulta === params.tipo || p.tipo_consulta === 'ambas'))
  // específico (primeira/retorno) ganha de 'ambas'
  candidatos.sort((a, b) => (a.tipo_consulta === 'ambas' ? 1 : 0) - (b.tipo_consulta === 'ambas' ? 1 : 0))
  const eleito = candidatos[0]
  return eleito ? { protocolo: eleito, versao: eleito.versao_atual! } : null
}
